import { Service } from '@angular/core';

import { ResourceType, TreeEntry } from '@models';

interface LeveledArray<T> extends Array<T> {
  level?: number;
}

export type TreeEntryArrayWithLevel = LeveledArray<TreeEntry>;

export type FinderTree = Nullable<TreeEntry | TreeEntry[]>;

interface Finder {
  tree: FinderTree;
  levels: Nullable<TreeEntryArrayWithLevel[]>;
  pathList: Nullable<TreeEntry[]>;
  finderSelectedItem: Nullable<number[]>;
}

type FinderLoadResult = {
  levels: Nullable<TreeEntryArrayWithLevel[]>;
  pathList: Nullable<TreeEntry[]>;
  finderSelectedItem: Nullable<number[]>;
  shouldScroll: boolean;
};

type TreeEntryWithChildren = Omit<TreeEntry, 'children'> & {
  children?: TreeEntryArrayWithLevel;
};

type LoadFinderRoom = TreeEntryWithChildren | TreeEntryArrayWithLevel;

@Service()
export class FinderService {
  levelCounter = -1;

  extractFinderState = ({
    levels,
    pathList,
    finderSelectedItem,
  }: FinderLoadResult) => ({ levels, pathList, finderSelectedItem });

  loadFinder = (
    room: LoadFinderRoom,
    level: number,
    levelsInput: Nullable<TreeEntryArrayWithLevel[]>,
    pathListInput: Nullable<TreeEntry[]>,
    finderSelectedItemInput: Nullable<number[]>
  ): FinderLoadResult => {
    let currentObjLevel: LoadFinderRoom = room;
    let shouldScroll = false;
    let levels = levelsInput;
    let pathList = pathListInput;
    let finderSelectedItem = finderSelectedItemInput;

    if (level === -1) {
      this.levelCounter = -1;
      levels = [];
      pathList = [];
      finderSelectedItem = [];
    } else {
      if (levels !== null) {
        const levelEntries = levels[level];

        if (!levelEntries) {
          return { levels, pathList, finderSelectedItem, shouldScroll };
        }

        currentObjLevel = levelEntries;
      }
    }

    const currentLevel = (currentObjLevel as TreeEntryArrayWithLevel).level;

    if (
      currentLevel !== undefined &&
      currentLevel < this.levelCounter &&
      levels
    ) {
      const { length } = levels;

      for (let i = length - 1; i > level; i--) {
        levels.splice(i, 1);
        finderSelectedItem!.splice(i, 1);

        this.levelCounter = level;
      }

      for (let i = length; i > level; i--) {
        pathList!.splice(i, 1);
      }

      this.levelCounter = level;
    }

    if (Array.isArray(currentObjLevel) && currentObjLevel.length > 0) {
      currentObjLevel.forEach((item: TreeEntryWithChildren) => {
        if (item.rid === (room as TreeEntryWithChildren).rid) {
          finderSelectedItem![this.levelCounter] = item.rid;
          this.levelCounter = this.levelCounter + 1;

          pathList!.push(room as TreeEntry);

          if (item.children !== undefined && item.children.length > 0) {
            item.children.level = this.levelCounter;
            levels!.push(item.children);

            shouldScroll = true;
          }
        }
      });
    } else {
      const currentEntry = currentObjLevel as TreeEntryWithChildren;
      const roomEntry = room as TreeEntryWithChildren;

      if (currentEntry.rid === roomEntry.rid) {
        this.levelCounter = this.levelCounter + 1;
        pathList!.push(roomEntry as unknown as TreeEntry);

        if (roomEntry.children !== undefined && roomEntry.children.length > 0) {
          roomEntry.children.level = this.levelCounter;
          levels!.push(roomEntry.children);

          shouldScroll = true;
        }
      }
    }

    return {
      levels,
      pathList,
      finderSelectedItem,
      shouldScroll,
    };
  };

  initFinder = (
    treeObj: TreeEntry,
    breadcrumb?: Nullable<{ rid: number }[]>,
    actualRoom?: Nullable<number>
  ): Finder => {
    let tree: FinderTree = null;
    let room: Nullable<LoadFinderRoom> = null;
    let tempFinderObj: Partial<Finder> = {};

    if (treeObj.type === ResourceType.COMMUNITY) {
      tree = treeObj.children;

      const firstEntry = Array.isArray(tree) ? tree[0] : undefined;

      if (firstEntry) {
        tempFinderObj = this.extractFinderState(
          this.loadFinder(firstEntry, -1, null, null, null)
        );
      }
    } else {
      if (treeObj.children?.length) {
        tree = treeObj;
        let level = -1;

        breadcrumb?.forEach((breadcrumbItem, $index) => {
          if ($index === 1) {
            room = tree;
            tempFinderObj.levels = null;
            tempFinderObj.pathList = null;
            tempFinderObj.finderSelectedItem = null;
            level = -1;
          } else if ($index > 1) {
            room = null;

            tempFinderObj.levels?.[level]?.forEach((levelItem) => {
              if (levelItem.rid === breadcrumbItem.rid) {
                room = levelItem;
              }
            });
          }

          if ($index > 0 && room !== null) {
            tempFinderObj = this.extractFinderState(
              this.loadFinder(
                room,
                level,
                tempFinderObj.levels ?? null,
                tempFinderObj.pathList ?? null,
                tempFinderObj.finderSelectedItem ?? null
              )
            );
            level += 1;
          }
        });

        room = null;

        if (level !== -1) {
          tempFinderObj.levels?.[level]?.forEach((levelItem) => {
            if (levelItem.rid === actualRoom) {
              room = levelItem;
            }
          });
        } else {
          room = tree;
        }

        if (room !== null) {
          tempFinderObj = this.extractFinderState(
            this.loadFinder(
              room,
              level,
              tempFinderObj.levels ?? null,
              tempFinderObj.pathList ?? null,
              tempFinderObj.finderSelectedItem ?? null
            )
          );
          level += 1;
        }
      } else {
        tree = treeObj;
        tempFinderObj = this.extractFinderState(
          this.loadFinder(tree, -1, null, null, null)
        );
      }
    }

    const { levels, pathList, finderSelectedItem } = tempFinderObj;

    return {
      tree,
      levels: levels || null,
      pathList: pathList || null,
      finderSelectedItem: finderSelectedItem || null,
    };
  };
}
